// workspace-tiles.ts — Rendu des tuiles du Workspace.
//
// Chaque tuile expose DEUX rendus : `full` (page `#workspace`) et `compact`
// (volet à 240 px). Mêmes données, même module, deux mises en forme — jamais
// deux implémentations, elles divergeraient.
//
// Le rendu compact perd le commentaire, jamais le chiffre : à 240 px, trois
// compteurs sur une ligne restent lisibles, mais une phrase d'explication ne
// l'est plus. C'est la popup « ? » qui la porte.
//
// L'atterrissage vient du SERVEUR (`tile.landing`), jamais reconstruit ici :
// c'est ce qui garantit que le clic ouvre exactement la population comptée.

import type { WorkspaceTile } from '../api/workspace.js';
import { mondayDmy } from '../domain/workspace-week.js';

export type TileMode = 'full' | 'compact';

type Data = Record<string, unknown>;

function num(data: Data | undefined, key: string): number {
  const v = data?.[key];
  return typeof v === 'number' ? v : 0;
}

function rows(data: Data | undefined, key: string): Data[] {
  const v = data?.[key];
  return Array.isArray(v) ? (v as Data[]) : [];
}

function str(row: Data, key: string): string {
  const v = row[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Couleur hexadécimale, ou `null`.
 *
 * La valeur vient du référentiel d'états du tenant, donc d'une saisie : elle
 * est injectée dans un attribut `style`, et rien ne garantit qu'elle soit une
 * couleur. On n'accepte que la forme `#rrggbb` plutôt que d'échapper, parce
 * qu'une déclaration CSS malformée ne se neutralise pas par échappement.
 */
function hexColor(value: string): string | null {
  return /^#[0-9a-f]{3,8}$/i.test(value) ? value : null;
}

/**
 * Libellé d'une tuile, paramètre compris (« Qualité MIGR1 », « Hygiène T01 »).
 *
 * Le code vient des données publiées par le serveur, pas d'un cache côté
 * page : le volet en a besoin autant que la page, et il ne charge ni projets
 * ni équipes. Un objet sans code, ou sorti du périmètre, laisse le titre nu.
 */
export function tileTitle(tile: WorkspaceTile): string {
  const base = t('workspace.tile.' + tile.key + '.title');
  const data = tile.data as Data | undefined;
  const code = data ? str(data, 'project_code') || str(data, 'team_code') : '';
  return code ? `${base} ${code}` : base;
}

// ── Fragments réutilisables ────────────────────────────────────────────────

/**
 * Trois compteurs cliquables indépendamment.
 *
 * Chaque chiffre porte SA facette : « 6 en retard » ouvre la liste des 6, pas
 * celle des 43. Les cibles font 44 px de haut au minimum — au pouce, il faut
 * de la marge autour de chacune, pas seulement autour de la tuile.
 */
function kpis(entries: Array<{ value: number; label: string; tone?: string; facet?: string }>) {
  return `<div class="ws-kpis">${entries
    .map(
      (e) => `<button type="button" class="ws-kpi${e.tone ? ' ws-' + e.tone : ''}"
        ${e.facet ? `data-facet="${esc(e.facet)}"` : ''}
        title="${esc(e.value + ' ' + e.label)}">
        <span class="ws-kpi-n">${e.value}</span>
        <span class="ws-kpi-l">${esc(e.label)}</span>
      </button>`,
    )
    .join('')}</div>`;
}

function deadlineKpis(data: Data | undefined) {
  return kpis([
    { value: num(data, 'late'), label: t('workspace.kpi.late'), tone: 'crit', facet: 'late' },
    { value: num(data, 'soon'), label: t('workspace.kpi.soon'), tone: 'warn', facet: 'soon' },
    { value: num(data, 'total'), label: t('workspace.kpi.total') },
  ]);
}

function emptyNote(mode: TileMode, key: string): string {
  // Y compris en compact : une carte réduite à son titre se lit comme un
  // défaut d'affichage, pas comme « rien à signaler ».
  const text = t('workspace.tile.' + key + '.empty');
  return `<p class="ws-empty${mode === 'compact' ? ' ws-empty-sm' : ''}">${esc(text)}</p>`;
}

// ── Un rendu par tuile ─────────────────────────────────────────────────────

function renderOwner(tile: WorkspaceTile, mode: TileMode): string {
  const n = num(tile.data, 'count');
  if (!n) return `<div class="ws-big ws-quiet">0</div>${emptyNote(mode, tile.key)}`;
  const chips: string[] = [];
  if (num(tile.data, 'late')) {
    chips.push(
      `<span class="ws-chip ws-crit">${num(tile.data, 'late')} ${esc(t('workspace.kpi.late'))}</span>`,
    );
  }
  if (num(tile.data, 'soon')) {
    chips.push(
      `<span class="ws-chip ws-warn">${num(tile.data, 'soon')} ${esc(t('workspace.kpi.soon'))}</span>`,
    );
  }
  return `<div class="ws-big ws-crit-text">${n}</div>
    ${chips.length ? `<div class="ws-chips">${chips.join('')}</div>` : ''}`;
}

function renderSinceSeen(tile: WorkspaceTile, mode: TileMode): string {
  const n = num(tile.data, 'new');
  const c = num(tile.data, 'changed');
  if (!n && !c) return emptyNote(mode, tile.key) || `<div class="ws-big ws-quiet">0</div>`;
  return kpis([
    { value: n, label: t('workspace.kpi.new'), tone: 'new', facet: 'new' },
    { value: c, label: t('workspace.kpi.changed'), tone: 'changed', facet: 'changed' },
  ]);
}

function renderByStatus(tile: WorkspaceTile, mode: TileMode): string {
  const list = rows(tile.data, 'statuses');
  if (!list.length) return emptyNote(mode, tile.key);
  const max = Math.max(...list.map((s) => num(s, 'named') + num(s, 'team')), 1);
  // En compact, les deux séries fusionnent : le volet n'a pas la largeur pour
  // les distinguer lisiblement, et la nuance passe dans la popup « ? ».
  return `<div class="ws-bars">${list
    .map((s) => {
      const named = num(s, 'named');
      const team = num(s, 'team');
      const total = named + team;
      const label = str(s, 'label') || t('workspace.status.none');
      const pctNamed = Math.round((named / max) * 100);
      const pctTeam = Math.round((team / max) * 100);
      // La couleur de l'état vient du référentiel du tenant : c'est celle que
      // l'utilisateur voit déjà sur la liste des actions, la retrouver ici lui
      // évite de relire les libellés. Repli sur l'accent quand l'état n'en
      // porte pas — un état sans couleur reste lisible.
      const tint = hexColor(str(s, 'color'));
      const fill = tint ? `background:${tint};` : '';
      // Les actions sans état n'ont pas de facette d'atterrissage : la barre
      // est rendue en `div`, pas en `button`. Un bouton qui ne mène nulle
      // part est pire qu'une ligne qui n'invite pas au clic.
      const inner = `<span class="ws-bar-l" title="${esc(label)}">${esc(label)}</span>
        <span class="ws-bar-track">
          <i class="ws-bar-named" style="${fill}width:${pctNamed}%"></i>
          ${mode === 'full' && team ? `<i class="ws-bar-team" style="${fill}width:${pctTeam}%"></i>` : ''}
        </span>
        <span class="ws-bar-v">${total}</span>`;
      if (s.status_id == null) return `<div class="ws-bar ws-bar-flat">${inner}</div>`;
      return `<button type="button" class="ws-bar" data-status="${esc(String(s.status_id))}">${inner}</button>`;
    })
    .join('')}</div>`;
}

function renderWorkload(tile: WorkspaceTile, mode: TileMode): string {
  const weeks = rows(tile.data, 'weeks');
  const late = num(tile.data, 'late');
  // Le retard est un STOCK cumulé, les semaines un FLUX. Les mettre à la même
  // échelle écrase la comparaison semaine à semaine — qui est tout l'objet de
  // cette tuile : un backlog de 263 retards réduirait six barres de 4 à des
  // traits indiscernables. Le retard garde donc sa propre barre, pleine
  // hauteur et séparée ; les semaines se comparent entre elles.
  const maxWeek = Math.max(...weeks.map((w) => num(w, 'count')), 1);
  const lateBar = late
    ? `<button type="button" class="ws-wk ws-wk-late" data-due-from="" data-due-to=""
         title="${esc(t('workspace.kpi.late'))} — ${late}">
         <b>${late}</b><i style="height:100%"></i>
         <span>${esc(t('workspace.kpi.lateShort'))}</span>
       </button>`
    : '';
  const weekBars = weeks
    .map((w) => {
      const n = num(w, 'count');
      // « S36 » ne se date pas de tête : l'infobulle donne le lundi qui ouvre
      // la semaine. Le compte n'y est pas répété, il est déjà sur la barre.
      const title = t('workspace.workloadWeek', {
        week: String(w.iso_week),
        date: mondayDmy(str(w, 'monday')),
      });
      return `<button type="button" class="ws-wk"
        data-due-from="${esc(str(w, 'start'))}" data-due-to="${esc(str(w, 'end'))}"
        title="${title}">
        <b>${n || ''}</b>
        <i style="height:${n ? Math.max(Math.round((n / maxWeek) * 100), 6) : 0}%"></i>
        <span>${mode === 'full' ? 'S' + esc(String(w.iso_week)) : ''}</span>
      </button>`;
    })
    .join('');
  return `<div class="ws-weeks">${lateBar}${lateBar ? '<span class="ws-wk-sep"></span>' : ''}${weekBars}</div>`;
}

function renderAgenda(tile: WorkspaceTile, mode: TileMode): string {
  const list = rows(tile.data, 'meetings');
  if (!list.length) return emptyNote(mode, tile.key);
  return `<div class="ws-rows">${list
    .map((m) => {
      const mine = num(m, 'my_actions');
      return `<a class="ws-row" href="#meeting/${esc(String(m.id))}">
        <span class="ws-row-t" title="${esc(str(m, 'label'))}">${esc(str(m, 'label'))}</span>
        <span class="ws-row-d">${esc(shortDate(str(m, 'date')))}${
          mine ? ` · <b>${mine}</b>` : ''
        }</span>
      </a>`;
    })
    .join('')}</div>`;
}

function renderGuides(tile: WorkspaceTile, mode: TileMode): string {
  const list = rows(tile.data, 'meetings');
  if (!list.length) return emptyNote(mode, tile.key);
  return `<div class="ws-rows">${list
    .map(
      (m) => `<a class="ws-row" href="#meeting/${esc(String(m.id))}">
        <span class="ws-row-t" title="${esc(str(m, 'label'))}">${esc(str(m, 'label'))}</span>
        <span class="ws-row-d">${esc(shortDate(str(m, 'date')))} ·
          ${num(m, 'prepare')}/${num(m, 'animate')}/${num(m, 'conclude')}</span>
      </a>`,
    )
    .join('')}
    ${mode === 'full' ? `<p class="ws-legend">${esc(t('workspace.tile.meeting_guides_pending.legend'))}</p>` : ''}`;
}

function renderHygiene(tile: WorkspaceTile): string {
  if (tile.data?.out_of_scope) {
    return `<p class="ws-empty">${esc(t('workspace.tile.hygiene.outOfScope'))}</p>`;
  }
  // Chaque compteur porte SA clé : les trois comptent des populations
  // différentes, donc atterrissent sur trois listes différentes
  // (`tile.landings[<clé>]`, publié par le serveur).
  return kpis([
    {
      value: num(tile.data, 'no_performer'),
      label: t('workspace.kpi.noPerformer'),
      tone: 'pink',
      facet: 'no_performer',
    },
    {
      value: num(tile.data, 'no_deadline'),
      label: t('workspace.kpi.noDeadline'),
      tone: 'warn',
      facet: 'no_deadline',
    },
    {
      value: num(tile.data, 'no_status'),
      label: t('workspace.kpi.noStatus'),
      tone: 'violet',
      facet: 'no_status',
    },
  ]);
}

/**
 * Le trou de délégation, en un seul chiffre.
 *
 * Un seul compteur : l'équipe est désignée, donc le travail est censé être
 * pris — mais personne n'est identifiable pour le faire. Deux ou trois
 * chiffres diraient moins bien cette chose-là.
 */
function renderHygieneTeam(tile: WorkspaceTile, mode: TileMode): string {
  if (tile.data?.out_of_scope) {
    return `<p class="ws-empty">${esc(t('workspace.tile.hygiene_team.outOfScope'))}</p>`;
  }
  const n = num(tile.data, 'unassigned');
  if (!n) return `<div class="ws-big ws-quiet">0</div>${emptyNote(mode, tile.key)}`;
  return `<div class="ws-big ws-crit-text">${n}</div>
    <div class="ws-chips"><span class="ws-chip ws-pink">${esc(
      t('workspace.kpi.noPerformer'),
    )}</span></div>`;
}

function shortDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

const BODIES: Record<string, (tile: WorkspaceTile, mode: TileMode) => string> = {
  actions_owner: renderOwner,
  actions_approver: (tile) => deadlineKpis(tile.data),
  actions_performer: (tile) => deadlineKpis(tile.data),
  actions_since_seen: renderSinceSeen,
  actions_by_status: renderByStatus,
  actions_workload: renderWorkload,
  meetings_agenda: renderAgenda,
  meeting_guides_pending: renderGuides,
  hygiene: renderHygiene,
  hygiene_team: renderHygieneTeam,
};

// ── Enveloppe ──────────────────────────────────────────────────────────────

export function renderTile(
  tile: WorkspaceTile,
  mode: TileMode,
  opts: { reorder?: boolean; flash?: boolean } = {},
): string {
  const parametrable = tile.parametrable === true;
  const title = tileTitle(tile);
  // Le clignotement est porté par une CLASSE, jamais par une animation posée
  // en JS : le rendu de la tuile est une chaîne, et re-générer l'élément
  // relance l'animation — c'est exactement ce qu'on veut à chaque changement.
  const flash = opts.flash ? ' ws-flash' : '';

  if (tile.revoked) {
    // Droit retiré depuis la pose : grisée avec une invite à la retirer,
    // jamais escamotée. Une tuile qui s'évapore sans explication est un
    // incident de support.
    return `<article class="ws-tile ws-revoked" data-tile="${tile.id}">
      <header class="ws-tile-h"><span class="ws-tile-n" title="${esc(title)}">${esc(title)}</span></header>
      <p class="ws-empty">${esc(t('workspace.revoked'))}</p>
      <button type="button" class="btn btn-secondary ws-remove" data-tile="${tile.id}">
        ${esc(t('workspace.remove'))}
      </button>
    </article>`;
  }

  const controls = opts.reorder
    ? `<span class="ws-tile-ic">
         <button type="button" class="ws-move" data-dir="up" data-tile="${tile.id}"
           aria-label="${esc(t('workspace.moveUp'))}" title="${esc(t('workspace.moveUp'))}">↑</button>
         <button type="button" class="ws-move" data-dir="down" data-tile="${tile.id}"
           aria-label="${esc(t('workspace.moveDown'))}" title="${esc(t('workspace.moveDown'))}">↓</button>
         <button type="button" class="ws-remove" data-tile="${tile.id}"
           aria-label="${esc(t('workspace.remove'))}" title="${esc(t('workspace.remove'))}">×</button>
       </span>`
    : `<span class="ws-tile-ic">
         ${
           parametrable
             ? `<button type="button" class="ws-gear" data-tile="${tile.id}"
                  aria-label="${esc(t('workspace.configure'))}" title="${esc(t('workspace.configure'))}">
                  <svg class="icon"><use href="/static/svg/icons.svg#icon-palette"></use></svg>
                </button>`
             : ''
         }
         <button type="button" class="ws-help" data-tile-key="${esc(tile.key)}"
           aria-label="${esc(t('workspace.what'))}"
           title="${esc(t('workspace.tile.' + tile.key + '.help'))}">?</button>
       </span>`;

  const body = (BODIES[tile.key] ?? (() => ''))(tile, mode);
  // Glissable dans les DEUX modes : « Réorganiser » ajoute les flèches (le
  // chemin tactile et accessible), il ne remplace pas le glisser-déposer.
  return `<article class="ws-tile ws-${esc(tile.size)}${flash}" data-tile="${tile.id}"
      data-key="${esc(tile.key)}" draggable="true">
    <header class="ws-tile-h">
      <span class="ws-tile-n" title="${esc(title)}">${esc(title)}</span>
      ${controls}
    </header>
    ${body}
    ${mode === 'full' && !opts.reorder ? `<button type="button" class="ws-land" data-tile="${tile.id}">${esc(t('workspace.open'))}</button>` : ''}
  </article>`;
}
